import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Globe, FileText, MapPin, CheckCircle2, XCircle, RefreshCw, Eye } from 'lucide-react';
import HelpBubble from './HelpBubble';
import { BACKEND_URL } from '../../utils/api';

const API = `${BACKEND_URL}/api`;

export default function AdminContentManager({ getAuthHeaders, activeTab }) {
  const tabMap = { 'blog': 'blog', 'press-releases': 'press', 'portfolio': 'portfolio' };
  const [blogPosts, setBlogPosts] = useState([]);
  const [pressReleases, setPressReleases] = useState([]);
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [showAddPortfolio, setShowAddPortfolio] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [indexNowLoading, setIndexNowLoading] = useState(false);
  const [indexNowResult, setIndexNowResult] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState(tabMap[activeTab] || 'blog');
  const [portfolioForm, setPortfolioForm] = useState({
    name: '', url: '', description: '', screenshot_url: '', category: 'Web Design',
    features: '', technologies: '', results: '', year_completed: new Date().getFullYear(),
    client_name: '', client_testimonial: '', published: true, featured: false,
  });

  useEffect(() => {
    if (activeSubTab === 'blog') fetchBlogPosts();
    if (activeSubTab === 'press') fetchPressReleases();
    if (activeSubTab === 'portfolio') fetchPortfolio();
  }, [activeSubTab]);

  const fetchBlogPosts = async () => {
    const headers = getAuthHeaders();
    try { const r = await axios.get(`${API}/admin/blog/posts`, { headers }); setBlogPosts(r.data.posts || r.data || []); } catch {}
  };

  const fetchPressReleases = async () => {
    const headers = getAuthHeaders();
    try { const r = await axios.get(`${API}/admin/press-releases`, { headers }); setPressReleases(r.data.press_releases || r.data || []); } catch {}
  };

  const fetchPortfolio = async () => {
    const headers = getAuthHeaders();
    try { const r = await axios.get(`${API}/admin/portfolio/all`, { headers }); setPortfolioProjects(r.data.projects || r.data || []); } catch {}
  };

  const resetPortfolioForm = () => {
    setPortfolioForm({ name: '', url: '', description: '', screenshot_url: '', category: 'Web Design', features: '', technologies: '', results: '', year_completed: new Date().getFullYear(), client_name: '', client_testimonial: '', published: true, featured: false });
  };

  const handleSavePortfolio = async () => {
    const headers = getAuthHeaders();
    const projectData = { ...portfolioForm, features: portfolioForm.features.split(',').map(f => f.trim()).filter(Boolean), technologies: portfolioForm.technologies.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editingPortfolio) { await axios.put(`${API}/admin/portfolio/${editingPortfolio}`, projectData, { headers }); }
      else { await axios.post(`${API}/admin/portfolio`, projectData, { headers }); }
      setShowAddPortfolio(false); setEditingPortfolio(null); resetPortfolioForm(); fetchPortfolio();
    } catch (error) { alert('Failed to save portfolio project'); }
  };

  const handleEditPortfolio = (project) => {
    setEditingPortfolio(project.id);
    setPortfolioForm({
      name: project.name || '', url: project.url || '', description: project.description || '',
      screenshot_url: project.screenshot_url || '', category: project.category || 'Web Design',
      features: (project.features || []).join(', '), technologies: (project.technologies || []).join(', '),
      results: project.results || '', year_completed: project.year_completed || new Date().getFullYear(),
      client_name: project.client_name || '', client_testimonial: project.client_testimonial || '',
      published: project.published !== false, featured: project.featured || false,
    });
    setShowAddPortfolio(true);
  };

  const handleDeletePortfolio = async (projectId, projectName) => {
    if (!window.confirm(`Delete "${projectName}"?`)) return;
    const headers = getAuthHeaders();
    try { await axios.delete(`${API}/admin/portfolio/${projectId}`, { headers }); fetchPortfolio(); } catch { alert('Failed to delete'); }
  };

  const submitToIndexNow = async (type = 'all') => {
    setIndexNowLoading(true);
    try {
      let endpoint;
      if (type === 'blogs') endpoint = '/api/seo/submit-blogs';
      else if (type === 'cities') endpoint = '/api/seo/submit-cities';
      else endpoint = '/api/seo/ping-indexnow';
      const response = await axios.post(`${BACKEND_URL}${endpoint}`);
      setIndexNowResult({ success: true, data: response.data });
    } catch (error) {
      setIndexNowResult({ success: false, error: error.response?.data?.detail || 'Failed to submit' });
    }
    setIndexNowLoading(false);
  };

  const seedBlogViews = async () => {
    const headers = getAuthHeaders();
    try { await axios.post(`${API}/admin/blog/seed-views`, {}, { headers }); alert('Blog views seeded!'); fetchBlogPosts(); } catch { alert('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-content-manager">
      <div className="flex items-center gap-2 border-b pb-4">
        {[{ id: 'blog', label: 'Blog Posts' }, { id: 'press', label: 'Press Releases' }, { id: 'portfolio', label: 'Portfolio' }].map(t => (
          <Button key={t.id} variant={activeSubTab === t.id ? 'default' : 'outline'} onClick={() => setActiveSubTab(t.id)} className={activeSubTab === t.id ? 'bg-blue-600 hover:bg-blue-700' : ''}>
            {t.label}
          </Button>
        ))}
        <HelpBubble
          title="How to Manage Content"
          steps={[
            'Blog Posts: View all blog posts, seed view counts, and manage publishing status',
            'Press Releases: View distributed press releases and their status',
            'Portfolio: Add/edit/delete portfolio projects shown on your website',
            'Use "IndexNow" to instantly notify search engines about new content',
            '"Submit All Content" pings Bing/Yandex with all your URLs at once',
            'To add a portfolio project: click "+ Add Project" → fill in details → Save',
            '"Featured" projects get highlighted placement on the portfolio page',
          ]}
        />
      </div>

      {activeSubTab === 'blog' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-slate-900">Blog Posts</h2>
            <div className="flex gap-2">
              <Button onClick={seedBlogViews} variant="outline" size="sm"><Eye className="h-4 w-4 mr-1" /> Seed Views</Button>
              <Button onClick={fetchBlogPosts} variant="outline">Refresh</Button>
            </div>
          </div>

          <Card className="bg-gradient-to-r from-blue-50 to-slate-50 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Globe className="h-5 w-5 text-blue-600" /> IndexNow - Instant Search Engine Notification</h3>
                  <p className="text-sm text-slate-600 mt-1">Instantly notify Bing, Yandex, and other search engines about new/updated content</p>
                </div>
                <Badge className="bg-green-600">Active</Badge>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button onClick={() => submitToIndexNow('all')} disabled={indexNowLoading} className="bg-blue-600 hover:bg-blue-700">
                  {indexNowLoading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : <><Globe className="h-4 w-4 mr-2" /> Submit All Content</>}
                </Button>
                <Button onClick={() => submitToIndexNow('blogs')} disabled={indexNowLoading} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50"><FileText className="h-4 w-4 mr-2" /> Blog Posts Only</Button>
                <Button onClick={() => submitToIndexNow('cities')} disabled={indexNowLoading} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50"><MapPin className="h-4 w-4 mr-2" /> City Pages Only</Button>
              </div>
              {indexNowResult && (
                <div className={`mt-4 p-4 rounded-lg ${indexNowResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {indexNowResult.success ? (
                    <div>
                      <p className="font-semibold text-green-800 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> Successfully Submitted to IndexNow!</p>
                      <div className="mt-2 text-sm text-green-700">
                        <p><strong>Total URLs:</strong> {indexNowResult.data?.total_urls_submitted || indexNowResult.data?.urls_submitted || 'N/A'}</p>
                        {indexNowResult.data?.url_breakdown && (<p className="mt-1">Core: {indexNowResult.data.url_breakdown.core_pages} | Cities: {indexNowResult.data.url_breakdown.city_pages} | Blogs: {indexNowResult.data.url_breakdown.blog_posts}</p>)}
                      </div>
                    </div>
                  ) : (<p className="text-red-800 flex items-center gap-2"><XCircle className="h-5 w-5" /> Error: {indexNowResult.error}</p>)}
                </div>
              )}
            </CardContent>
          </Card>

          {blogPosts.length === 0 ? (
            <Card><CardContent className="py-12 text-center">No blog posts yet</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {blogPosts.map((post) => (
                <Card key={post.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                        <p className="text-sm text-slate-600 mb-2">Keyword: {post.keyword}</p>
                        <p className="text-sm text-slate-500">{new Date(post.created_at).toLocaleDateString()}</p>
                        <p className="text-sm mt-2">Views: {post.views || 0}</p>
                      </div>
                      <Badge className={post.published ? 'bg-green-600' : 'bg-gray-500'}>{post.published ? 'Published' : 'Draft'}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'press' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center"><h2 className="text-2xl font-bold text-slate-900">Press Releases</h2><Button onClick={fetchPressReleases} variant="outline">Refresh</Button></div>
          {pressReleases.length === 0 ? (
            <Card><CardContent className="py-12 text-center">No press releases yet</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {pressReleases.map((pr) => (
                <Card key={pr.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">{pr.headline}</h3>
                        <p className="text-sm text-slate-600 mb-2">Topic: {pr.topic}</p>
                        <p className="text-sm text-slate-500">{new Date(pr.created_at).toLocaleDateString()}</p>
                        {pr.distributed && <p className="text-sm text-green-600 mt-2">Distributed</p>}
                      </div>
                      <Badge className={pr.published ? 'bg-green-600' : 'bg-gray-500'}>{pr.published ? 'Published' : 'Draft'}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'portfolio' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Portfolio Management</h2>
            <div className="flex gap-2">
              <Button onClick={() => { if (showAddPortfolio) { setShowAddPortfolio(false); setEditingPortfolio(null); resetPortfolioForm(); } else { setEditingPortfolio(null); resetPortfolioForm(); setShowAddPortfolio(true); } }} className="bg-blue-600 hover:bg-blue-700">
                {showAddPortfolio ? 'Cancel' : '+ Add Project'}
              </Button>
              <Button onClick={fetchPortfolio} variant="outline">Refresh</Button>
            </div>
          </div>

          {showAddPortfolio && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">{editingPortfolio ? 'Edit Project' : 'Add New Project'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Project Name *</label><Input value={portfolioForm.name} onChange={(e) => setPortfolioForm({...portfolioForm, name: e.target.value})} placeholder="e.g., NetAuctionsInc.com" /></div>
                  <div><label className="block text-sm font-medium mb-1">URL *</label><Input value={portfolioForm.url} onChange={(e) => setPortfolioForm({...portfolioForm, url: e.target.value})} placeholder="https://example.com" /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Description *</label><Textarea value={portfolioForm.description} onChange={(e) => setPortfolioForm({...portfolioForm, description: e.target.value})} placeholder="Brief description..." rows={3} /></div>
                  <div><label className="block text-sm font-medium mb-1">Screenshot URL</label><Input value={portfolioForm.screenshot_url} onChange={(e) => setPortfolioForm({...portfolioForm, screenshot_url: e.target.value})} placeholder="https://..." /></div>
                  <div><label className="block text-sm font-medium mb-1">Category</label>
                    <select value={portfolioForm.category} onChange={(e) => setPortfolioForm({...portfolioForm, category: e.target.value})} className="w-full border rounded-md px-3 py-2">
                      <option value="Web Design">Web Design</option><option value="E-Commerce">E-Commerce</option><option value="SEO">SEO</option><option value="Web Application">Web Application</option><option value="Directory">Directory</option><option value="Marketing">Marketing</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium mb-1">Features (comma-separated)</label><Input value={portfolioForm.features} onChange={(e) => setPortfolioForm({...portfolioForm, features: e.target.value})} placeholder="Frontend, Backend, E-Commerce" /></div>
                  <div><label className="block text-sm font-medium mb-1">Technologies (comma-separated)</label><Input value={portfolioForm.technologies} onChange={(e) => setPortfolioForm({...portfolioForm, technologies: e.target.value})} placeholder="React, Node.js, MongoDB" /></div>
                  <div><label className="block text-sm font-medium mb-1">Results/Highlights</label><Input value={portfolioForm.results} onChange={(e) => setPortfolioForm({...portfolioForm, results: e.target.value})} placeholder="e.g., 200% increase in traffic" /></div>
                  <div><label className="block text-sm font-medium mb-1">Year Completed</label><Input type="number" value={portfolioForm.year_completed} onChange={(e) => setPortfolioForm({...portfolioForm, year_completed: parseInt(e.target.value)})} /></div>
                  <div><label className="block text-sm font-medium mb-1">Client Name</label><Input value={portfolioForm.client_name} onChange={(e) => setPortfolioForm({...portfolioForm, client_name: e.target.value})} /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Client Testimonial</label><Textarea value={portfolioForm.client_testimonial} onChange={(e) => setPortfolioForm({...portfolioForm, client_testimonial: e.target.value})} rows={2} /></div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={portfolioForm.published} onChange={(e) => setPortfolioForm({...portfolioForm, published: e.target.checked})} className="rounded" /><span className="text-sm">Published</span></label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={portfolioForm.featured} onChange={(e) => setPortfolioForm({...portfolioForm, featured: e.target.checked})} className="rounded" /><span className="text-sm">Featured</span></label>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleSavePortfolio} className="bg-blue-600 hover:bg-blue-700">{editingPortfolio ? 'Update Project' : 'Add Project'}</Button>
                  <Button variant="outline" onClick={() => { setShowAddPortfolio(false); setEditingPortfolio(null); resetPortfolioForm(); }}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {portfolioProjects.length === 0 ? (
            <Card><CardContent className="py-12 text-center">No portfolio items yet. Click "Add Project" to get started!</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {portfolioProjects.map((project) => (
                <Card key={project.id} className={project.featured ? 'border-blue-400 border-2' : ''}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold">{project.name}</h3>
                      <div className="flex gap-1">
                        {project.featured && <Badge className="bg-blue-500">Featured</Badge>}
                        <Badge className={project.published ? 'bg-green-600' : 'bg-gray-500'}>{project.published ? 'Live' : 'Draft'}</Badge>
                      </div>
                    </div>
                    <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline mb-3 block">{project.url}</a>
                    <p className="text-sm mb-4 text-gray-600">{project.description}</p>
                    <div className="flex flex-wrap gap-2 mb-3">{project.features?.map((f, i) => <Badge key={i} variant="outline">{f}</Badge>)}</div>
                    {project.technologies?.length > 0 && (<div className="flex flex-wrap gap-1 mb-3">{project.technologies.map((t, i) => <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">{t}</span>)}</div>)}
                    {project.results && <p className="text-sm text-green-600 font-semibold mb-3">{project.results}</p>}
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <Button size="sm" variant="outline" onClick={() => handleEditPortfolio(project)}>Edit</Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeletePortfolio(project.id, project.name)}>Delete</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
